import { component } from '@diligentcorp/atlas-design-tokens/lens';
import React, { ReactNode, useState } from 'react';
import {
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import { AuditEntryType } from 'src/graphql';

export interface AuditLogColumn {
  label: string;
  truncate?: boolean; // defaults to true
  cellSx?: SxProps<Theme>;
}

export interface AuditLogPaginatedTableProps {
  entries: AuditEntryType[];
  columns: AuditLogColumn[];
  renderRow: (entry: AuditEntryType) => ReactNode[];
  pageSize?: number;
  emptyState?: ReactNode;
  ariaLabel?: string;
}

const DEFAULT_PAGE_SIZE = 5;

/**
 * Paginated table for audit log entries. Mount inside `<AtlasMuiThemeProvider>`.
 *
 * No custom styling — table chrome and pagination resolve from the Atlas
 * theme defaults applied to MUI primitives.
 */
export const AuditLogPaginatedTable: React.FC<AuditLogPaginatedTableProps> = ({
  entries,
  columns,
  renderRow,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyState,
  ariaLabel,
}) => {
  const [page, setPage] = useState(1);

  if (entries.length === 0) {
    return <div data-testid="audit-log-empty">{emptyState ?? null}</div>;
  }

  const pageCount = Math.ceil(entries.length / pageSize);
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const visible = entries.slice(start, start + pageSize);

  return (
    <Box>
      <Table aria-label={ariaLabel} size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.label}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((entry, index) => (
            <TableRow
              key={entry.id}
              data-testid={`audit-log-row-${entry.id ?? index}`}
            >
              {renderRow(entry).map((cell, idx) => {
                const col = columns[idx];
                if ((col?.truncate ?? true) && typeof cell === 'string') {
                  return (
                    <TableCell
                      key={idx}
                      sx={{
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...col?.cellSx,
                      }}
                    >
                      <Tooltip title={cell} placement="top">
                        <span>{cell}</span>
                      </Tooltip>
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={idx} sx={col?.cellSx}>
                    {cell}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pageCount > 1 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: component.table.footer.container.marginTop.value,
          }}
        >
          <Pagination
            count={pageCount}
            page={safePage}
            onChange={(_event, value) => setPage(value)}
          />
        </Box>
      ) : null}
    </Box>
  );
};
